import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import type { CallSheetProgressDto, CallSheetDto } from '@signalvote/shared';
import { MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { useCallSheetsProgress, useCreateCallSheet, useDeleteCallSheet, useProspectBookSummary, useRenameCallSheet } from '@/hooks/queries';
import { getCampaignTodayString } from '@/lib/calendarUtils';
import { cn } from '@/lib/utils';
import { dayOrClock, shortDay } from './activityDay';
import { CallSheetNameDialog, DeleteCallSheetDialog } from './CallSheetDialogs';
import { FundraisingPage } from './FundraisingPage';

const count = new Intl.NumberFormat('en-US');

/**
 * The Call books tab: every book with its people, how many have been called from it and the call-backs
 * owed — the same counts the Caller sees on the phone — with renaming and deleting from each row's menu and
 * "New call book" in the header. A book works like a label: one person can be on several (ADR-089).
 */
export function CallSheets() {
  const navigate = useNavigate();
  const { data: books, isLoading, isError } = useCallSheetsProgress();
  // The book's summary carries each book's token, which a rename or delete sends back — not the whole book.
  const { data: summary } = useProspectBookSummary();
  const { mutate: create, error: createError, reset: resetCreate } = useCreateCallSheet();
  const { mutate: rename, error: renameError, reset: resetRename } = useRenameCallSheet();
  const { mutate: remove, error: removeError, reset: resetRemove } = useDeleteCallSheet();
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState<CallSheetDto | null>(null);
  const [deleting, setDeleting] = useState<CallSheetDto | null>(null);
  const tokens = new Map((summary?.sheets ?? []).map((sheet) => [sheet.id, sheet]));
  const today = getCampaignTodayString();

  return (
    <FundraisingPage
      tab="call-books"
      action={
        <Button
          className="h-10"
          onClick={() => {
            resetCreate();
            setCreating(true);
          }}
        >
          New call book
        </Button>
      }
    >
      <div className="space-y-4">
        {isLoading && (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        )}
        {isError && !isLoading && <p className="text-sm text-muted-foreground">We couldn’t load the call books right now.</p>}
        {books && books.length === 0 && (
          <p className="rounded-xl border bg-card px-5 py-10 text-center text-[13px] text-muted-foreground">
            No call books yet. Select people on the Prospects tab and add them to a call book, or start one with New call
            book.
          </p>
        )}
        {books && books.length > 0 && (
          <div className="overflow-hidden rounded-xl border bg-card">
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead className="border-b text-left text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                  <tr className="h-10">
                    <th className="px-5 font-semibold">Book</th>
                    <th className="px-3 text-right font-semibold">People</th>
                    <th className="px-3 font-semibold">Called</th>
                    <th className="px-3 font-semibold">Call-backs owed</th>
                    <th className="px-3 font-semibold">Last call</th>
                    <th className="w-14" />
                  </tr>
                </thead>
                <tbody>
                  {books.map((progress) => (
                    <BookRow
                      key={progress.id}
                      progress={progress}
                      today={today}
                      sheet={tokens.get(progress.id)}
                      onRename={(sheet) => {
                        resetRename();
                        setRenaming(sheet);
                      }}
                      onDelete={(sheet) => {
                        resetRemove();
                        setDeleting(sheet);
                      }}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <p className="max-w-3xl text-xs text-muted-foreground">
          A book’s order is fixed: largest suggested ask at the time each person was added, then name. Adding people later
          never reshuffles the people already on it. Anyone who asks not to be called is hidden from every book straight away,
          without anyone editing a book.
        </p>
      </div>

      <CallSheetNameDialog
        open={creating}
        onOpenChange={setCreating}
        title="New call book"
        submitLabel="Create"
        error={createError?.message ?? null}
        onSubmit={(name) =>
          create(
            { name, prospectIds: [] },
            {
              onSuccess: (result) => {
                setCreating(false);
                void navigate({ to: '/admin/fundraising/call-books/$sheetId', params: { sheetId: result.sheet.id } });
              },
            },
          )
        }
      />
      {renaming && (
        <CallSheetNameDialog
          open
          onOpenChange={(open) => !open && setRenaming(null)}
          title={`Rename ${renaming.name}`}
          initialName={renaming.name}
          submitLabel="Save"
          error={renameError?.message ?? null}
          onSubmit={(name) => rename({ sheet: renaming, name }, { onSuccess: () => setRenaming(null) })}
        />
      )}
      {deleting && (
        <DeleteCallSheetDialog
          open
          onOpenChange={(open) => !open && setDeleting(null)}
          name={deleting.name}
          size={books?.find((b) => b.id === deleting.id)?.people ?? deleting.size}
          error={removeError?.message ?? null}
          onConfirm={() => remove({ sheet: deleting }, { onSuccess: () => setDeleting(null) })}
        />
      )}
    </FundraisingPage>
  );
}

function BookRow({
  progress,
  today,
  sheet,
  onRename,
  onDelete,
}: {
  progress: CallSheetProgressDto;
  /** The campaign's today, `yyyy-MM-dd`. */
  today: string;
  /** Its token, from the book's summary; absent for a moment after a create. */
  sheet: CallSheetDto | undefined;
  onRename: (sheet: CallSheetDto) => void;
  onDelete: (sheet: CallSheetDto) => void;
}) {
  const { people, called, callBacksDue, hiddenByDoNotCall } = progress;
  const meta = [
    `Created by ${progress.createdByName ?? '—'}`,
    shortDay(progress.createdOn),
    ...(hiddenByDoNotCall > 0 ? [`${count.format(hiddenByDoNotCall)} hidden (Do Not Call)`] : []),
  ].join(' · ');
  const done = people > 0 && called >= people;
  const percent = people > 0 ? Math.round((Math.min(called, people) / people) * 100) : 0;
  return (
    <tr className="h-14 border-b last:border-0">
      <td className="min-w-56 px-5 py-2">
        <Link
          to="/admin/fundraising/call-books/$sheetId"
          params={{ sheetId: progress.id }}
          className="font-semibold text-foreground hover:underline"
        >
          {progress.name}
        </Link>
        <span className="block text-xs text-muted-foreground">{meta}</span>
      </td>
      <td className="px-3 text-right tabular-nums">{count.format(people)}</td>
      <td className="px-3">
        <span className={cn('block tabular-nums', done && 'font-semibold text-success')}>
          {done ? `All ${count.format(people)} called` : `${count.format(called)} of ${count.format(people)}`}
        </span>
        <span aria-hidden="true" className="mt-1 block h-1 w-32 overflow-hidden rounded-sm bg-muted">
          <span className={cn('block h-full', done ? 'bg-success' : 'bg-primary')} style={{ width: `${percent}%` }} />
        </span>
      </td>
      <td className={cn('px-3 tabular-nums', callBacksDue > 0 ? 'text-warning' : 'text-muted-foreground')}>
        {callBacksDue > 0 ? `${count.format(callBacksDue)} owed` : '—'}
      </td>
      <td className="whitespace-nowrap px-3 tabular-nums text-muted-foreground">
        {progress.lastCalledAt && progress.lastCalledOn ? dayOrClock(progress.lastCalledOn, progress.lastCalledAt, today) : 'Not called yet'}
      </td>
      <td className="pr-3 text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-11 w-11" aria-label={`More for ${progress.name}`} disabled={!sheet}>
              <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <Link to="/admin/fundraising/call-books/$sheetId" params={{ sheetId: progress.id }}>
                Open book
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => sheet && onRename(sheet)}>Rename</DropdownMenuItem>
            <DropdownMenuItem className="text-destructive" onSelect={() => sheet && onDelete(sheet)}>
              Delete book…
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );
}
